/* import __COLOCATED_TEMPLATE__ from './active-sla.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';

interface Arguments {
  nextBreachAt: Date;
  isInteractive?: boolean;
}

interface Signature {
  Args: Arguments;
  Element: HTMLElement;
}

enum Status {
  Due,
  NearlyDue,
  Overdue,
}

const FIVE_MINS = 5 * 60 * 1000;
const SIXTY_SECONDS = 60;

export default class ActiveSla extends Component<Signature> {
  @service declare intl: IntlService;

  get status(): Status | undefined {
    let now = new Date();

    if (this.args.nextBreachAt < now) {
      return Status.Overdue;
    }

    let isNearlyDue = now.getTime() + FIVE_MINS > this.args.nextBreachAt.getTime();
    if (isNearlyDue) {
      return Status.NearlyDue;
    }

    return Status.Due;
  }

  get formattedTimeRemaining(): string {
    let now = new Date();
    let diffMs = this.args.nextBreachAt.getTime() - now.getTime();
    let diffSeconds = Math.round(Math.abs(diffMs) / 1000);

    // For times under 1 minute, show exact seconds
    if (diffSeconds < SIXTY_SECONDS) {
      return `${diffSeconds}s`;
    }

    // For 1 minute and above, round to nearest unit (matching previous format-relative behavior)
    let minutes = Math.round(diffSeconds / 60);
    if (minutes < 60) {
      return `${minutes}m`;
    }

    let hours = Math.round(minutes / 60);
    if (hours < 24) {
      return `${hours}h`;
    }

    let days = Math.round(hours / 24);
    return `${days}d`;
  }

  get percentageFilled(): number {
    return {
      [Status.Due]: 10,
      [Status.NearlyDue]: 60,
      [Status.Overdue]: 0,
    }[this.status ?? Status.Due];
  }

  get statusStyles() {
    switch (this.status) {
      case Status.Due:
        return 'text-muted bg-neutral-container border-neutral-border ';
      case Status.NearlyDue:
        return 'text-default bg-notice-container border-notice-border ';
      case Status.Overdue:
        return 'text-default bg-error-container border-error-border ';
      default:
        return '';
    }
  }

  get interactiveStyles() {
    if (!this.args.isInteractive) {
      return '';
    }

    switch (this.status) {
      case Status.Due:
        return 'hover:border-neutral-border focus:border-neutral-border  ';
      case Status.Overdue:
        return 'hover:border-error-border focus:border-error-border  ';
      case Status.NearlyDue:
        return 'hover:border-notice-border-emphasis focus:border-notice-border-emphasis  ';
      default:
        return '';
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Sla::ActiveSla': typeof ActiveSla;
    'inbox2/sla/active-sla': typeof ActiveSla;
  }
}
